/**
 * @file GlobalSearch.tsx
 * @description Global search for entire site - bikeparks, snowparks, and content pages
 * Used in navigation bar
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import { getBikeparks, getSnowparks, getPages } from '@/lib/api';
import { useTranslations } from 'next-intl';
import { Search, X, Bike, Snowflake, FileText } from 'lucide-react';

interface SearchResult {
  type: 'bikepark' | 'snowpark' | 'page';
  id: string;
  title: string;
  description: string;
  url: string;
}

export default function GlobalSearch() {
  const t = useTranslations('common');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Initialize from URL
  useEffect(() => {
    const urlQuery = searchParams.get('search');
    if (urlQuery) {
      setQuery(urlQuery);
    }
  }, [searchParams]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }

      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        // Use API functions instead of direct fetch - they handle locale properly
        const bikeparks = await getBikeparks('pl');
        const snowparks = await getSnowparks('pl');
        const pages = await getPages('pl');

        const searchQuery = query.toLowerCase();
        const searchResults: SearchResult[] = [];

        // Filter bikeparks
        bikeparks.forEach((park: any) => {
          const parkName = park.name || '';
          const parkRegion = park.region || '';
          const parkBlurb = park.blurb || '';

          if (
            parkName.toLowerCase().includes(searchQuery) ||
            parkRegion.toLowerCase().includes(searchQuery) ||
            parkBlurb.toLowerCase().includes(searchQuery)
          ) {
            searchResults.push({
              type: 'bikepark',
              id: park.documentId || park.id,
              title: parkName,
              description: parkBlurb || parkRegion,
              url: `/bikepark/${park.slug}`,
            });
          }
        });

        // Filter snowparks
        snowparks.forEach((park: any) => {
          const parkName = park.name || '';
          const parkRegion = park.region || '';
          const parkBlurb = park.blurb || '';

          if (
            parkName.toLowerCase().includes(searchQuery) ||
            parkRegion.toLowerCase().includes(searchQuery) ||
            parkBlurb.toLowerCase().includes(searchQuery)
          ) {
            searchResults.push({
              type: 'snowpark',
              id: park.documentId || park.id,
              title: parkName,
              description: parkBlurb || parkRegion,
              url: `/snowpark/${park.slug}`,
            });
          }
        });

        // Filter pages
        pages.forEach((page: any) => {
          const pageTitle = page.title || '';
          const pageContent = page.content || '';

          if (
            pageTitle.toLowerCase().includes(searchQuery) ||
            pageContent.toLowerCase().includes(searchQuery)
          ) {
            searchResults.push({
              type: 'page',
              id: page.documentId || page.id,
              title: pageTitle,
              description: pageContent.substring(0, 100) + '...',
              url: `/${page.slug}`,
            });
          }
        });

        setResults(searchResults.slice(0, 10)); // Max 10 results
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleResultClick = (url: string) => {
    router.push(url);
    setIsOpen(false);
    setQuery('');
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'bikepark':
        return <Bike size={18} strokeWidth={2} />;
      case 'snowpark':
        return <Snowflake size={18} strokeWidth={2} />;
      case 'page':
        return <FileText size={18} strokeWidth={2} />;
      default:
        return <Search size={18} strokeWidth={2} />;
    }
  };

  return (
    <div className="global-search" ref={searchRef}>
      <div className="search-input-wrapper">
        <span className="search-icon"><Search size={16} strokeWidth={2} /></span>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={t('searchPlaceholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {query && (
          <button
            className="search-clear"
            onClick={() => {
              setQuery('');
              setResults([]);
            }}
            aria-label={t('clear')}
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (query || results.length > 0) && (
        <div className="search-results">
          {loading && (
            <div className="search-loading">
              <div className="search-spinner"></div>
              Szukam...
            </div>
          )}

          {!loading && results.length === 0 && query && (
            <div className="search-no-results">
              Nie znaleziono wyników dla "{query}"
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="search-results-list">
              {results.map((result) => (
                <button
                  key={result.id}
                  className="search-result-item"
                  onClick={() => handleResultClick(result.url)}
                >
                  <span className="result-icon">{getResultIcon(result.type)}</span>
                  <div className="result-content">
                    <div className="result-title">{result.title}</div>
                    <div className="result-description">{result.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .global-search {
          position: relative;
          width: 100%;
          max-width: 400px;
        }

        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          padding: 6px 12px;
          transition: all 200ms ease;
        }

        .search-input-wrapper:focus-within {
          background: white;
          border-color: rgba(16, 185, 129, 0.6);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .search-icon {
          font-size: 16px;
          margin-right: 8px;
          opacity: 0.6;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          color: #1a3a2e;
        }

        .search-input::placeholder {
          color: rgba(26, 58, 46, 0.5);
        }

        .search-clear {
          background: none;
          border: none;
          color: rgba(26, 58, 46, 0.5);
          cursor: pointer;
          font-size: 14px;
          padding: 0 4px;
          transition: color 150ms ease;
        }

        .search-clear:hover {
          color: #1a3a2e;
        }

        .search-results {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: white;
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          max-height: 400px;
          overflow-y: auto;
          z-index: 1000;
          animation: slideDown 200ms ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .search-loading {
          padding: 32px 20px;
          text-align: center;
          color: rgba(26, 58, 46, 0.6);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .search-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(16, 185, 129, 0.2);
          border-top-color: #10b981;
          border-radius: 50%;
          animation: spin 600ms linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .search-no-results {
          padding: 32px 20px;
          text-align: center;
          color: rgba(26, 58, 46, 0.6);
        }

        .search-results-list {
          padding: 8px;
        }

        .search-result-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          border: none;
          background: transparent;
          border-radius: 8px;
          width: 100%;
          text-align: left;
          cursor: pointer;
          transition: all 150ms ease;
        }

        .search-result-item:hover {
          background: rgba(16, 185, 129, 0.08);
        }

        .result-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .result-content {
          flex: 1;
          min-width: 0;
        }

        .result-title {
          font-size: 14px;
          font-weight: 600;
          color: #1a3a2e;
          margin-bottom: 4px;
        }

        .result-description {
          font-size: 12px;
          color: rgba(26, 58, 46, 0.6);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* Mobile */
        @media (max-width: 768px) {
          .global-search {
            max-width: none;
          }

          .search-results {
            max-height: 300px;
          }

          .search-input {
            font-size: 16px; /* Prevent zoom on iOS */
          }
        }
      `}</style>
    </div>
  );
}
